import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Button, CircularProgress, Paper, Tooltip, Typography } from '@mui/material';
import React from 'react';

import { useImpersonationStatus, useStopImpersonation } from './util/useImpersonation.util';

const ACCENT = '#B26A00';

/**
 * Compact floating pill shown at the bottom while the session is impersonating another user.
 * Rendered for every authenticated user but renders nothing unless impersonation is active.
 */
export const ImpersonationBanner: React.FC = () => {
  const { status } = useImpersonationStatus();
  const { isStopping, stopImpersonation } = useStopImpersonation();

  if (!status?.impersonating) {
    return null;
  }

  const impersonated = status.impersonatedUser;
  const name =
    [impersonated?.firstName, impersonated?.lastName].filter(Boolean).join(' ') ||
    impersonated?.email ||
    'użytkownik';

  const tooltip = [
    impersonated?.email,
    status.realAdmin?.email ? `zalogowany jako ${status.realAdmin.email}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Tooltip arrow placement="top" title={tooltip}>
      <Paper
        elevation={6}
        sx={{
          alignItems: 'center',
          bgcolor: '#fff',
          border: `1px solid ${ACCENT}59`,
          borderRadius: 999,
          bottom: 16,
          display: 'inline-flex',
          gap: 0.75,
          left: '50%',
          maxWidth: '92vw',
          pl: 0.75,
          position: 'fixed',
          pr: 0.5,
          py: 0.5,
          transform: 'translateX(-50%)',
          zIndex: 1300,
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: `${ACCENT}1f`,
            borderRadius: '50%',
            color: ACCENT,
            display: 'flex',
            flexShrink: 0,
            height: 22,
            justifyContent: 'center',
            width: 22,
          }}
        >
          <VisibilityIcon sx={{ fontSize: 14 }} />
        </Box>

        <Typography
          sx={{
            color: 'rgba(0, 0, 0, 0.82)',
            fontSize: 12.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          variant="caption"
        >
          Działasz jako <b>{name}</b>
        </Typography>

        <Button
          disabled={isStopping}
          onClick={() => stopImpersonation()}
          size="small"
          sx={{
            '&:hover': { bgcolor: `${ACCENT}14` },
            borderRadius: 999,
            color: ACCENT,
            fontSize: 12,
            fontWeight: 700,
            minWidth: 0,
            px: 1,
            py: 0.25,
            textTransform: 'none',
          }}
          variant="text"
        >
          {isStopping && <CircularProgress size={12} sx={{ mr: 0.5 }} />}
          Wróć
        </Button>
      </Paper>
    </Tooltip>
  );
};

